import type {
  ProLayoutProps,
  Settings as ProLayoutSettings,
} from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter, useIntl } from '@umijs/max';
import { AiChat } from '@zhiyue/ai-chat';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  TenantSwitchModal,
  useSession,
  useShell,
  useTenant,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import { BankOutlined, HomeOutlined } from '@ant-design/icons';
import { App, theme as antdTheme } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  Question,
  SelectLang,
  ThemeToggle,
} from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { getMyAuthorizedResources } from './services/bff/permission.api';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.href = applicationReturnTo;
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

const CurrentTenant: React.FC = () => {
  const { currentTenantUuid, tenants } = useTenant();
  const [tenantModalOpen, setTenantModalOpen] = React.useState(false);
  const currentTenant = tenants.find(
    (tenant) => tenant.id === currentTenantUuid,
  );

  if (!currentTenant) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="app-current-tenant"
        title={currentTenant.name}
        aria-label={`切换企业，当前：${currentTenant.name}`}
        onClick={() => setTenantModalOpen(true)}
      >
        <span className="app-current-tenant-icon" aria-hidden="true">
          <BankOutlined />
        </span>
        <span className="app-current-tenant-name">{currentTenant.name}</span>
      </button>
      <TenantSwitchModal
        open={tenantModalOpen}
        onClose={() => setTenantModalOpen(false)}
        labels={{
          title: '切换企业',
          empty: '暂无可切换企业',
          switchAction: '切换企业',
          createAction: '创建/加入企业',
          deleteAction: '删除企业',
        }}
      />
    </>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <App>
        <SessionSync setInitialState={setInitialState} />
        {children}
        <FloatingAiChat />
      </App>
    </SessionProvider>
  );
};

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();

  if (
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  ) {
    return null;
  }

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          (
            currentUser as CurrentUser & {
              organizationUuid?: string;
              lastLoginOrganizationUuid?: string;
            }
          ).organizationUuid ||
          (
            currentUser as CurrentUser & {
              lastLoginOrganizationUuid?: string;
            }
          ).lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = tenantList.some(
        (tenant) =>
          tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const settingsToken = (initialState?.settings as ProLayoutProps | undefined)
    ?.token;
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    className: 'crm-shell crm-content-theme',
    contentWidth: 'Fluid',
    headerHeight: 44,
    token: {
      ...settingsToken,
      header: {
        ...settingsToken?.header,
        heightLayoutHeader: 44,
        colorBgHeader: 'var(--crm-nav-bg, var(--app-nav-background))',
        colorBgScrollHeader: 'var(--crm-nav-bg, var(--app-nav-background))',
      },
      pageContainer: {
        ...settingsToken?.pageContainer,
        colorBgPageContainer: 'var(--crm-page-bg, var(--app-content-background))',
      },
    },
    menu: {
      defaultOpenAll: true,
      ignoreFlatMenu: true,
      autoClose: false,
      ...(initialState?.settings as any)?.menu,
    },
    contentStyle: {
      height: 'calc(100dvh - 44px)',
      overflow: 'auto',
      minWidth: 0,
      padding: 0,
    },
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <CurrentTenant key="current-tenant" />,
        <NotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    breadcrumbRender: false,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          {children}
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
